import React, { useState, useMemo } from 'react';
import { Search, ChevronUp, ChevronDown, Package } from 'lucide-react';

const CuttingPlanPartsList = ({ parts = [], loading }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('part_code');
  const [sortDirection, setSortDirection] = useState('asc');

  const filteredAndSortedParts = useMemo(() => {
    let result = [...parts];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(part =>
        part.part_code?.toLowerCase().includes(term) ||
        part.part_name?.toLowerCase().includes(term) ||
        part.material_name?.toLowerCase().includes(term)
      );
    }

    result.sort((a, b) => {
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      const comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [parts, searchTerm, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4" />
    ) : (
      <ChevronDown className="w-4 h-4" />
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-full"></div>
          <div className="h-4 bg-gray-200 rounded w-full"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (!parts || parts.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Peças do Plano</h2>
        <div className="text-center py-8">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Nenhuma peça encontrada neste plano</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Peças do Plano ({filteredAndSortedParts.length})
        </h2>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar peça..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm w-64"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                onClick={() => handleSort('part_code')}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div className="flex items-center gap-1">
                  Código
                  <SortIcon field="part_code" />
                </div>
              </th>
              <th
                onClick={() => handleSort('part_name')}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div className="flex items-center gap-1">
                  Nome
                  <SortIcon field="part_name" />
                </div>
              </th>
              <th
                onClick={() => handleSort('material_name')}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div className="flex items-center gap-1">
                  Material
                  <SortIcon field="material_name" />
                </div>
              </th>
              <th
                onClick={() => handleSort('width')}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div className="flex items-center gap-1">
                  Dimensões (mm)
                  <SortIcon field="width" />
                </div>
              </th>
              <th
                onClick={() => handleSort('quantity')}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div className="flex items-center gap-1">
                  Qtd
                  <SortIcon field="quantity" />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fita de Borda
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAndSortedParts.map((part) => (
              <tr key={part.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="font-medium text-gray-900">{part.part_code}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-gray-700">{part.part_name}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-gray-600">{part.material_name || '-'}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-gray-700">
                    {part.width} x {part.height} x {part.thickness}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-gray-700">{part.quantity}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 text-xs text-gray-500">
                    {part.edge_band_top > 0 && <span>T:{part.edge_band_top}</span>}
                    {part.edge_band_bottom > 0 && <span>B:{part.edge_band_bottom}</span>}
                    {part.edge_band_left > 0 && <span>L:{part.edge_band_left}</span>}
                    {part.edge_band_right > 0 && <span>R:{part.edge_band_right}</span>}
                    {!part.edge_band_top && !part.edge_band_bottom && !part.edge_band_left && !part.edge_band_right && (
                      <span className="text-gray-400">-</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CuttingPlanPartsList;
